import React, { useCallback } from 'react';
import { Chip, useTheme } from 'react-native-paper';
import { DrawerNavigationProp } from '@react-navigation/drawer';
import { CompositeScreenProps, useNavigation } from '@react-navigation/native';
import { Linking, View } from 'react-native';
import { StackScreenProps } from '@react-navigation/stack';
import { t } from 'i18n-js';
import { useI18n } from '../../context/I18nContext';
import { screenNames } from '../../navigators/screenNames';
import ScrollablePage from '../../components/ui/ScrollablePage';
import { AppDrawerScreenProp } from '../../components/DrawerMenuContent';
import { AuthState, useAuth } from '../../context/AuthContext';
import { ListItemRow, ListItemRowProps } from '../../components/ListItemRow';
import {
  Currency,
  Product,
  ProductPaymentType,
  ProductRenewInterval,
  ProductSubscriptionStatus,
} from '../../types/appsync-types';
import Typography from '../../components/Typography';
import { UiIcon } from '../../components/SimpleListIcon';
import { SubscriptionsStackRouteParams } from '../../navigators/SubscriptionsStackNavigator';
import { useGetProductsByPodId } from '../../apis/subscriptionApis';
import Loading from '../../components/Loading';
import { useMapViewFilter } from '../../context/MapViewFilterContext';
import { getRenewIntervalTranslationKeys } from '../../utils/getRenewIntervalTranslationKeys';
import { useRefetchOnFocus } from '../../hooks/useRefetchOnFocus';
import { useGetSpaceTerms } from '../../apis/mapApis';
import { useParkingTermsUrlForCurrentLocale } from '../../hooks/useParkingTermsUrlForCurrentLocale';

function SubscriptionItem({
  name,
  productId,
  price,
  owner,
  currency,
  active,
  status,
  renewalDate,
  renewInterval,
}: {
  name: string;
  productId: string;
  price: number | undefined | null; // TODO: What to do if price is undefined or null?
  owner: string | undefined | null;
  currency: Currency;
  active: boolean;
  status: ProductSubscriptionStatus | undefined;
  renewalDate: string | undefined | null;
  renewInterval: ProductRenewInterval;
}) {
  const { formatCurrency, formatDate } = useI18n();
  const { colors } = useTheme();
  const { I18n } = useI18n();
  const { timeZone } = useMapViewFilter();
  const navigation = useNavigation<DrawerNavigationProp<any>>();

  const onPress = useCallback(() => {
    navigation.navigate(screenNames.SubscriptionDetailScreen, {
      productId,
    });
  }, [navigation, productId]);

  let subscriptionStatusText: string | null = null;
  let icon: ListItemRowProps['leftIcon'] = { name: 'calendar', set: 'ion' };
  if (active) {
    switch (status) {
      case ProductSubscriptionStatus.CANCELLED:
        subscriptionStatusText = I18n.t('subscriptions.activeUntil', {
          date: renewalDate
            ? formatDate(new Date(renewalDate), 'dateWithYear', timeZone)
            : I18n.t('subscriptions.endOfBillingPeriod'),
        });
        icon = { name: 'check', set: 'material' };
        break;
      case ProductSubscriptionStatus.CANCELLING:
        subscriptionStatusText = I18n.t('subscriptions.cancelling');
        icon = { name: 'check', set: 'material' };
        break;
      case ProductSubscriptionStatus.REQUIRES_ACTION:
        subscriptionStatusText = I18n.t('subscriptions.actionRequired');
        icon = { name: 'alert-circle', set: 'material-community' };
        break;
      case ProductSubscriptionStatus.PROCESSING:
        subscriptionStatusText = I18n.t('subscriptions.processing');
        icon = { name: 'progress-clock', set: 'material-community' };
        break;
      case ProductSubscriptionStatus.FAILED:
        subscriptionStatusText = I18n.t('subscriptions.failed');
        icon = { name: 'close-circle', set: 'material-community' };
        break;
      default:
        subscriptionStatusText = I18n.t('subscriptions.active');
        icon = { name: 'check', set: 'material' };
        break;
    }
  }

  const renderTitle = () => {
    return (
      <Typography variant="body1">
        {name}
        {subscriptionStatusText && (
          <Typography variant="body1">
            {' • '}
            <Typography variant="body1" color={colors.primary} style={{ fontWeight: 'bold' }}>
              {subscriptionStatusText}
            </Typography>
          </Typography>
        )}
      </Typography>
    );
  };

  const descriptionOwner = owner ? `${owner} •` : '';

  return (
    <ListItemRow
      title={renderTitle}
      description={`${descriptionOwner} ${
        formatCurrency(price, currency) + ' / ' + I18n.t(getRenewIntervalTranslationKeys(renewInterval).abbreviation)
      }`}
      leftIcon={icon}
      onPress={onPress}
      accessibilityRole="button"
      type="navigation"
    />
  );
}

export type SubscriptionsRouteParams = {
  filter?: string;
  podId?: string;
};

type Props = CompositeScreenProps<
  StackScreenProps<SubscriptionsStackRouteParams, 'Subscriptions'>,
  AppDrawerScreenProp
>;

export const SubscriptionsScreen: React.FC<Props> = ({ route }) => {
  const { I18n } = useI18n();
  const { colors } = useTheme();
  const { state } = useAuth();
  const { filter: filterParam, podId } = route.params || {};
  const [filter, setFilter] = React.useState<string | null>(filterParam || null);

  const getProductsResult = useGetProductsByPodId(podId);
  useRefetchOnFocus(getProductsResult.refetch);

  const getSpaceTermsResult = useGetSpaceTerms(route.params?.podId);
  const parkingTermsUrl = useParkingTermsUrlForCurrentLocale(
    getSpaceTermsResult.data?.data?.getPoisByID?.metadata?.parkingTerms,
  );

  const products =
    (getProductsResult.data?.data?.getProducts ?? getProductsResult.data?.data?.getProductsByPodId)?.filter<Product>(
      (product): product is Product => !!product && product.paymentType === ProductPaymentType.SUBSCRIPTION,
    ) ?? [];

  const isActiveSubscriptionsView = filterParam === 'active';

  const filteredProducts = filter
    ? products.filter((sub) => (filter === 'active' ? !!sub.subscription : !sub.subscription))
    : products;

  const renderNoSubscriptionsContent = () => {
    return getProductsResult.isLoading ? (
      <Loading />
    ) : (
      <Typography variant="body1" style={{ paddingHorizontal: 16, paddingVertical: 8 }}>
        {filter === 'active' ? t('subscriptions.noActiveSubscriptions') : t('subscriptions.noProducts')}
      </Typography>
    );
  };

  return (
    <ScrollablePage
      summary={{
        title: I18n.t('subscriptions.summaryTitle'),
        description: isActiveSubscriptionsView
          ? I18n.t('subscriptions.activeSummaryDescription')
          : I18n.t('subscriptions.summaryDescription'),
        icon: isActiveSubscriptionsView ? 'calendar' : 'basket',
        button: parkingTermsUrl
          ? {
              label: I18n.t('subscriptions.terms'),
              onPress: () => {
                Linking.openURL(parkingTermsUrl);
              },
            }
          : undefined,
      }}
    >
      {state !== AuthState.GUEST_LOGIN && !filterParam && (
        <View style={{ flexDirection: 'row', paddingLeft: 16, paddingBottom: 8, alignItems: 'center' }}>
          <UiIcon size={24} name="sliders" set="fontawesome6" color={colors.onSurface} />
          {['active', 'inactive'].map((item) => {
            const selected = filter === item;
            return (
              <Chip
                key={item}
                style={{ marginLeft: 8, backgroundColor: selected ? colors.primary : undefined }}
                mode={selected ? 'flat' : 'outlined'}
                selectedColor={selected ? colors.inverseOnSurface : undefined}
                selected={selected}
                onPress={(e) => (selected ? setFilter(null) : setFilter(item))}
              >
                {I18n.t(`subscriptions.filter.${item}`)}
              </Chip>
            );
          })}
        </View>
      )}
      {/* TODO: Should subscriptions be grouped by region or something? */}
      {filteredProducts.length
        ? filteredProducts?.map((product) => (
            <SubscriptionItem
              key={product.id}
              name={product.name ?? ''}
              productId={product.id ?? ''}
              price={product.subscription?.price ?? product.price}
              owner={product.owner?.name}
              currency={(product.subscription?.currency as Currency) || Currency.EUR}
              active={!!product.subscription}
              status={product.subscription?.status}
              renewalDate={product.subscription?.renewalDate}
              renewInterval={product.renewInterval ?? ProductRenewInterval.MONTHLY}
            />
          ))
        : renderNoSubscriptionsContent()}
    </ScrollablePage>
  );
};
